<template>
  <PalettePickerTextEditor
    ref="dropdown"
    :boundary="'viewport'"
    :color="colorChange.color"
    :alpha="colorChange.alpha"
    @change="onChange"
    @show="onShow"
    @hide="onHide"
  >
    <template #icon>
      <BtnToggle 
        :title="$t('engine.manageText.modalWidget.tooltip.lblHighlightColor', languageEngine)"
      >
        <IconTextBgColor 
          v-show="!colorHex" 
          class="-mt-5" 
          :style="`height: 16px; color: transparent;`"
        />
        <IconTextBgColor 
          v-show="Boolean(colorHex && colorHexThreshold <= 0.98)" 
          class="-mt-5" 
          :class="{ 
            'svg-path-dark': Boolean(colorHex && colorHexThreshold > 0.6),
            'svg-path-white': Boolean(colorHex && colorHexThreshold <= 0.6),
          }" 
          :style="`height: 16px; color: ${colorHex};`"
        />
        <IconTextBgColorLight
          v-show="Boolean(colorHex && colorHexThreshold > 0.98)"
          class="-mt-5" 
          :style="`height: 16px; color: ${colorHex};`"
        />
      </BtnToggle>
    </template>
  </PalettePickerTextEditor>
</template>

<script>
import { mapGetters } from "vuex";
import PalettePickerTextEditor from "@components_system/Shared/Tools/PalettePicker/PalettePickerTextEditor";
import BtnToggle from "./_BtnToggle";
import IconTextBgColor from "@components_system/Shared/Images/Icon/IconTextBgColor";
import IconTextBgColorLight from "@components_system/Shared/Images/Icon/IconTextBgColorLight";

export default {
  components: {
    PalettePickerTextEditor,
    BtnToggle,
    IconTextBgColor,
    IconTextBgColorLight,
  },
  data() {
    return {
      colorChange: {
        color: '#FFFFFF',
        alpha: 100,
      },
    }
  },
  computed: {
    ...mapGetters({
      languageEngine: "designIframe/userPreference/languageEngine",
      texteditor: "designIframe/texteditor/texteditor",
      templateColor: "designIframe/metadata/templateColor",
    }),
    format() {
      return this.texteditor.selectionFormatMerge;
    },
    value() {
      if (Array.isArray(this.format.background)) return this.format.background[0];
      return this.format.background;
    },
    _value() {
      if (this.value && this.value.includes('rgba(')) return this.rgbaToColorAlpha(this.value); // rgba(var(--tpc-11), 100%)
      if (this.value && this.value.includes('rgb(')) return { color: this.rgbToColor(this.value), alpha: 100 }; // rgb(var(--tpc-11)), rgb(0, 0, 0)
      if (this.value && this.value.includes('#')) return { color: this.value, alpha: 100 }; // #000000
      return { 
        color: '#FFFFFF', 
        alpha: 0,
      };
    },
    color() {
      // --tpc-11
      // #000000
      return this._value.color;
    },
    alpha() {
      // 100
      return this._value.alpha;
    },
    colorHex() {
      if (this._value.color) {
        return this.colorToHex(this._value.color);
      }
      return this._value.color;
    },
    colorHexThreshold() {
      return this.$helper.color.hexThreshold(this.colorHex);
    },
  },
  methods: {
    rgbaToColorAlpha(value) {
      // rgba(var(--tpc-11), 100%)
      // rgba(var(--tpc-11), 1)
      // rgba(0, 0, 0, 100%)
      // rgba(0, 0, 0, 1)
      value = value.trim().replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, '');
      let valueArr = value.split(',').map(str => str.trim());
      if (valueArr.length == 2) {
        return {
          color: valueArr[0].replace('var(', '').replace(')', ''),
          alpha: this.alphaToPercent(valueArr[1]),
        }
      } else if (valueArr.length == 4) {
        return {
          color: this.colorToHex(valueArr.slice(0, 3).join(",")),
          alpha: this.alphaToPercent(valueArr[3]),
        }
      }
      return { color: undefined, alpha: undefined };
    },
    rgbToColor(value) {
      // rgb(var(--tpc-11))
      // rgb(0, 0, 0)
      value = value.trim().replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, '');
      let valueArr = value.split(',').map(str => str.trim());
      if (value.includes('--tpc')) {
        return valueArr[0].replace('var(', '').replace(')', ''); // --tpc-11
      } else {
        return this.colorToHex(valueArr.slice(0, 3).join(",")).toUpperCase(); // #000000
      }
    },

    colorToHex(value) {
      if (value) {
        value = value.trim();

        if (value.startsWith("#")) { // #xxxxxx
          return value.toUpperCase();

        } else if (value.startsWith("--tpc-")) { // --tpc-15
          return this.tcpToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgba(var(--tpc-")) {  // rgba(var(--tpc-15), 100%)
          return this.rgbaTpcToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgb(var(--tpc-")) {  // rgb(var(--tpc-15))
          return this.rgbTpcToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgba(")) {  // rgba(x,x,x,100%)
          return this.rgbaToHex(value).toUpperCase();
          
        } else if (value.startsWith("rgb(")) {  // rgb(x,x,x)
          return this.rgbToHex(value).toUpperCase();
          
        } else if (value.match(/\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}/)) {  // x,x,x
          let arrRGB = value.split(',').map(str => str.trim());
          return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
        }
      }
      return value;
    },
    tcpToHex(value) {
      // --tpc-15
      if (value) {
        let tpc = value.trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbaTpcToHex(value) {
      // rgba(var(--tpc-15), 100%)
      if (value) {
        value = value.replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, ''); // var(--tpc-15), 100%
        value = value.replace('var(', '').replace(')', ''); // --tpc-15, 100%
        let arrValue = value.split(',').map(str => str.trim()); // [--tpc-15, 100%]
        let tpc = arrValue[0].trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbTpcToHex(value) {
      // rgb(var(--tpc-15))
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, ''); // var(--tpc-15)
        value = value.replace('var(', '').replace(')', ''); // --tpc-15
        let tpc = value.trim(); // --tpc-15
        let color = this.templateColor[tpc]; //  x,x,x
        let arrRGB = color.split(",").map(str => str.trim()); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbaToHex(value) {
      // rgba(x,x,x,100%)
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgba\(/g, '').replaceAll(/\)$/g, ''); // x,x,x,100%
        let arrValue = value.split(',').map(str => str.trim()); // [x,x,x,100%]
        let arrRGB = arrValue.slice(0, 3); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },
    rgbToHex(value) {
      // rgb(x,x,x)
      if (value) {
        value = value.trim();
        value = value.replaceAll(/^rgb\(/g, '').replaceAll(/\)$/g, ''); // x,x,x
        let arrValue = value.split(',').map(str => str.trim()); // [x,x,x]
        let arrRGB = arrValue.slice(0, 3); // [x,x,x]
        return this.$helper.color.rgbToHex(arrRGB[0], arrRGB[1], arrRGB[2]).toUpperCase();
      }
      return value;
    },

    alphaToPercent(value) {
      if (value === undefined || value === null) return 100;
      value = value.toString().trim();
      if (value.includes('%')) { // 50% => 50
        value = value.replaceAll('%', ''); 
        value = Math.min(Math.max(value, 0), 100);
        return value;
      }
      value = Number(value) * 100;
      value = Math.min(Math.max(value, 0), 100);
      return value;
    },

    onShow() {
      this.$store.dispatch("designIframe/texteditor/setData", { selection: this.texteditor.editor.getSelection() });
      this.$store.dispatch("designIframe/texteditor/setData", { backgroundpicker: true });
    },
    onHide() {
      this.$store.dispatch("designIframe/texteditor/setData", { selection: undefined });
      this.$store.dispatch("designIframe/texteditor/setData", { backgroundpicker: false });
      this.texteditor.editor.focus();
    },
    onChange(value) {
      const selection = this.texteditor.selection;
      if (!selection) return;

      this.colorChange.color = value.color;
      this.colorChange.alpha = parseInt(value.alpha);

      let formatValue = value.alpha ? this.$helper.color.getColor(value.color, value.alpha) : false; // rgba(var(--tpc-15), 100%)
      
      this.texteditor.editor.format('background', formatValue);
      this.texteditor.editor.formatText({index: selection.index + selection.length, length: 0}, { background: formatValue }); // For Last LineBlank
      this.$refs.dropdown.show(); // continue show
    },
  },
  mounted() {
    this.colorChange = JSON.parse(JSON.stringify(this._value));
  },
  watch: {
    _value(newValue) {
      this.colorChange = JSON.parse(JSON.stringify(newValue));
    },
  },
}
</script>
